import { doc, setDoc } from "firebase/firestore";

import getOfflineMessage from "../../helpers/getOfflineMessage";

const FAILED_WRITE_ID = "writeFailureMessage";

// One banner however many writes fail. A player who loses one write is very
// likely about to lose the next, and stacking a red bar per question would bury
// the quiz under its own error messages.
//
// The wording stays vague about what was lost on purpose. This wrapper carries
// answers, quizCompletedOn and the whole initial user document, so "answers may
// need to be repeated" would be wrong for two of its three callers. Telling the
// player to reload and check is true for all of them.
//
// The error code is in the message because there is nowhere else for it to go.
// .babelrc strips every console.* call in every environment, and this app has no
// error reporting, so on a real device the code below reaches nobody. Without it
// permission-denied, invalid-data and an expired token are indistinguishable in
// a bug report.
function reportFailedWrite(error) {
  if (document.getElementById(FAILED_WRITE_ID)) return;

  const code = error?.code ? ` (${error.code})` : "";
  const messageEl = getOfflineMessage(
    `Some of your progress could not be saved${code}. Please reload the page to check what was recorded.`
  );
  messageEl.id = FAILED_WRITE_ID;

  const body = document.querySelector("body");
  body.insertBefore(messageEl, body.firstChild);
}

// Deliberately NOT awaited by callers, and this does not change that.
//
// persistentLocalCache does not settle a write promise until the server
// acknowledges it, so awaiting here would block the quiz for as long as a player
// is offline — which is exactly the case the offline pack exists to support. The
// write queuing durably and flushing on reconnect is the design, not a bug.
//
// The bug was the missing catch. A rejection — permission denied, invalid data,
// a token that expired mid-round — surfaced as an unhandled promise rejection
// and the player was told nothing at all, which is how progress went missing
// silently. Note a log alone would be no better: .babelrc strips every console.*
// call in every environment, so on a real device it reaches nobody. It has to
// reach the screen.
//
// Offline is not a failure here and must not raise the banner: the promise stays
// pending rather than rejecting, so this catch never runs.
//
// The ORIGINAL promise is returned, not the one .catch() produces. Returning the
// catch's result would hand back a promise that always fulfils, so a caller that
// did await could not tell a failed write from a successful one. Attaching the
// handler is what prevents the unhandled rejection; returning the underlying
// promise keeps the error available to anyone who wants it. A caller that awaits
// therefore has to handle rejection itself — none does today.
export default function firebaseSetDocWrapper(
  db,
  collectionName,
  userDocumentID,
  userData
) {
  console.log("In firebaseSetDocWrapper...");

  const userRef = doc(db, collectionName, userDocumentID);

  const write = setDoc(
    userRef,
    {
      ...userData
    },
    { merge: true }
  );

  write.catch(error => {
    console.log("Could not save to Firestore:", error);
    reportFailedWrite(error);
  });

  return write;
}
